import { useContext, useEffect, useRef } from 'react';
import { GuardianContext } from '../../components/GuardianProvider';
import { useChat } from '../../hooks/useChat';

/**
 * The auto-summary landing turn. Sent hidden (kept out of the feed) so the agent's reply is
 * the visible landing message.
 */
const DASHBOARD_SUMMARY_PROMPT =
  'Give me a brief overview of this dashboard: what the main charts show and any notable ' +
  'trends, spikes, or anomalies worth attention.';

/**
 * Fires one hidden "summarize this dashboard" turn per fresh dashboard conversation, once the
 * dashboard has rendered.
 */
export const useGetDashboardSummaryOnLoad = ({
  conversationId,
  ready,
}: {
  conversationId: string | undefined;
  ready: boolean;
}): void => {
  const { conversation, sendMessageAsync } = useChat();
  const { conversationScope } = useContext(GuardianContext);
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ready || !conversationId) return;
    // Guard the mount race: the dashboard's conversation must already be the live one.
    if (conversationScope !== 'dashboard') return;
    if (firedForRef.current === conversationId) return;
    // Only greet a clean conversation — never inject a summary into an ongoing chat.
    if (!conversation.isNew) return;
    firedForRef.current = conversationId;
    sendMessageAsync({ query: DASHBOARD_SUMMARY_PROMPT, hidden: true }).catch(() => {
      if (firedForRef.current === conversationId) firedForRef.current = null;
    });
  }, [ready, conversationId, conversationScope, conversation.isNew, sendMessageAsync]);
};
